import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, 
  Save,
  KeyRound
} from 'lucide-react';

const CompanyForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    login_url: '',
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isValidUrl, setIsValidUrl] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  useEffect(() => {
    if (isEditing) {
      fetchCompany();
    }
  }, [id, isEditing]);

  const fetchCompany = async () => {
    try {
      const response = await axios.get(`/companies/${id}`);
      setFormData(response.data);
    } catch (error) {
      toast.error('Error loading company data');
      navigate('/dashboard');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors({...validationErrors, [name]: ''});
    }

    // Real-time URL validation
    if (name === 'login_url') {
      setIsValidUrl(validateUrl(value));
    }
  };

  const validateUrl = (url) => {
    if (!url) return true; // Empty URL is handled by required validation
    try {
      const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
      new URL(formattedUrl);
      return true;
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Company name is required';
    }
    
    if (!formData.login_url.trim()) {
      errors.login_url = 'Login URL is required';
    } else if (!validateUrl(formData.login_url)) {
      errors.login_url = 'Please enter a valid URL';
    }
    
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }
    
    if (!formData.password.trim()) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        login_url: formData.login_url.startsWith('http') 
          ? formData.login_url 
          : `https://${formData.login_url}`
      };

      if (isEditing) {
        await axios.put(`/companies/${id}`, submitData);
        toast.success('Company updated successfully');
      } else {
        await axios.post('/companies', submitData);
        toast.success('Company added successfully');
      }
      
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 
        (isEditing ? 'Error updating company' : 'Error adding company');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getFieldStatus = (fieldName) => {
    if (validationErrors[fieldName]) return 'error';
    if (fieldName === 'login_url' && formData.login_url && !isValidUrl) return 'error';
    if (formData[fieldName] && !validationErrors[fieldName]) return 'success';
    return 'default';
  };

  const getFieldBorderClass = (fieldName) => {
    const status = getFieldStatus(fieldName);
    switch (status) {
      case 'error': return 'border-red-300 focus:border-red-500 focus:ring-red-500/20';
      case 'success': return 'border-green-300 focus:border-green-500 focus:ring-green-500/20';
      default: return 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Enhanced Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-2.5 rounded-xl shadow-lg">
                <KeyRound className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {isEditing ? 'Edit Company' : 'Add New Company'}
                </h1>
                <p className="text-sm text-gray-500">
                  {isEditing ? 'Update company access information' : 'Add secure access credentials'}
                </p>
              </div>
            </div>
            <Badge variant={isEditing ? "secondary" : "default"} className="px-3 py-1">
              {isEditing ? 'Editing' : 'Creating'}
            </Badge>
          </div>
        </div>
      </header>

      {/* Enhanced Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center space-x-2 mb-8">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              <span className="text-sm font-medium text-blue-600">Company Information</span>
            </div>
          </div>

          {/* Main Form Card */}
          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center pb-8">
              <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                {isEditing ? 'Update Company Details' : 'Company Access Setup'}
              </CardTitle>
              <p className="text-gray-600 max-w-md mx-auto">
                {isEditing 
                  ? 'Modify the login credentials and company information below'
                  : 'Enter your company login details to enable secure auto-login functionality'
                }
              </p>
            </CardHeader>
            
            <CardContent className="px-8 pb-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Company Name Field */}
                <div className="space-y-3">
                  <Label htmlFor="name" className="text-sm font-semibold text-gray-700">
                    Company Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    className={`h-12 text-base ${getFieldBorderClass('name')} transition-all duration-200`}
                    placeholder="Ex: Acme Corporation Inc."
                    required
                  />
                  {validationErrors.name && (
                    <span className="text-sm text-red-600">{validationErrors.name}</span>
                  )}
                </div>

                {/* Login URL Field */}
                <div className="space-y-3">
                  <Label htmlFor="login_url" className="text-sm font-semibold text-gray-700">
                    Login Page URL
                  </Label>
                  <Input
                    id="login_url"
                    name="login_url"
                    type="url"
                    value={formData.login_url}
                    onChange={handleChange}
                    className={`h-12 text-base ${getFieldBorderClass('login_url')} transition-all duration-200`}
                    placeholder="https://company.com/login"
                    required
                  />
                  {validationErrors.login_url && (
                    <span className="text-sm text-red-600">{validationErrors.login_url}</span>
                  )}
                  {!validationErrors.login_url && formData.login_url && (
                    <span className="text-sm text-gray-500">Complete URL where the login form is located</span>
                  )}
                </div>

                {/* Username Field */}
                <div className="space-y-3">
                  <Label htmlFor="username" className="text-sm font-semibold text-gray-700">
                    Username / Email
                  </Label>
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    value={formData.username}
                    onChange={handleChange}
                    className={`h-12 text-base ${getFieldBorderClass('username')} transition-all duration-200`}
                    placeholder="user@company.com"
                    required
                  />
                  {validationErrors.username && (
                    <span className="text-sm text-red-600">{validationErrors.username}</span>
                  )}
                </div>

                {/* Password Field */}
                <div className="space-y-3">
                  <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                    Password
                  </Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`h-12 text-base ${getFieldBorderClass('password')} transition-all duration-200`}
                    placeholder="••••••••••••"
                    required
                  />
                  {validationErrors.password && (
                    <span className="text-sm text-red-600">{validationErrors.password}</span>
                  )}
                  {!validationErrors.password && formData.password && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="text-blue-700">
                        <span className="text-sm font-medium">Security Note</span>
                      </div>
                      <p className="text-sm text-blue-600 mt-1">
                        This password will be encrypted with AES-256 and stored securely
                      </p>
                    </div>
                  )}
                </div>

                {/* Submit Actions */}
                <div className="flex flex-col sm:flex-row gap-4 pt-6">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:opacity-50"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>{isEditing ? 'Updating...' : 'Saving...'}</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <Save className="h-5 w-5" />
                        <span>{isEditing ? 'Update Company' : 'Save Company'}</span>
                      </div>
                    )}
                  </Button>
                  
                  <Link to="/dashboard" className="flex-1">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-12 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-medium rounded-xl transition-all duration-300"
                    >
                      Cancel
                    </Button>
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Security Information Card */}
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                <div className="bg-green-100 p-2 rounded-lg flex-shrink-0 w-10 h-10"></div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-green-900 text-lg">
                    Advanced Security Features
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-green-800">
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      <span>AES-256 encryption for all credentials</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      <span>Secure transmission over HTTPS</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      <span>Zero-knowledge architecture</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      <span>Regular security audits</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default CompanyForm;